import { FunctionComponent, useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import DirectionsIcon from '@mui/icons-material/Directions';
import styles from './LocationDirections.module.css';

// Component to adjust map bounds
const MapBounds: FunctionComponent<{ 
  providerLocation: { lat: number; lng: number };
  userLocation: { lat: number; lng: number } | null;
}> = ({ providerLocation, userLocation }) => {
  const map = useMap();
  
  useEffect(() => {
    if (userLocation) {
      // Fit map to show both markers
      const bounds = L.latLngBounds([
        [providerLocation.lat, providerLocation.lng],
        [userLocation.lat, userLocation.lng]
      ]);
      map.fitBounds(bounds, { padding: [50, 50] });
    } else {
      // Center on provider location
      map.setView([providerLocation.lat, providerLocation.lng], 14);
    }
  }, [map, providerLocation, userLocation]);
  
  return null;
};

// Fix for default Leaflet icon issue in React
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom provider marker icon
const providerIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="20" cy="20" r="18" fill="#009951" stroke="white" stroke-width="3"/>
      <path d="M20 10C16.686 10 14 12.686 14 16C14 20 20 28 20 28C20 28 26 20 26 16C26 12.686 23.314 10 20 10ZM20 19C18.343 19 17 17.657 17 16C17 14.343 18.343 13 20 13C21.657 13 23 14.343 23 16C23 17.657 21.657 19 20 19Z" fill="white"/>
    </svg>
  `),
  iconSize: [40, 40],
  iconAnchor: [20, 40],
  popupAnchor: [0, -40],
});

// Custom user location marker
const userLocationIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="15" cy="15" r="8" fill="#2196F3" stroke="white" stroke-width="2"/>
      <circle cx="15" cy="15" r="3" fill="white"/>
    </svg>
  `),
  iconSize: [30, 30],
  iconAnchor: [15, 15],
  popupAnchor: [0, -15],
});

interface LocationDirectionsProps {
  providerName: string;
  providerAddress: {
    street: string;
    city: string;
    state: string;
    zip: string;
  };
  providerCoordinates: {
    lat: number;
    lng: number;
  };
  mapHeight?: string;
}

const LocationDirections: FunctionComponent<LocationDirectionsProps> = ({
  providerName,
  providerAddress,
  providerCoordinates,
  mapHeight = '300px'
}) => {
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.error('Error getting user location:', error);
          // Default to San Francisco center if location access denied
          setUserLocation({ lat: 37.7749, lng: -122.4194 });
        }
      );
    }
  }, []);

  const handleDirections = () => {
    if (userLocation) {
      // Open Google Maps with directions
      const origin = `${userLocation.lat},${userLocation.lng}`;
      const destination = `${providerCoordinates.lat},${providerCoordinates.lng}`;
      const url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}`;
      window.open(url, '_blank');
    } else {
      // If no user location, just show the provider location
      const url = `https://www.google.com/maps/search/?api=1&query=${providerCoordinates.lat},${providerCoordinates.lng}`;
      window.open(url, '_blank');
    }
  };

  return (
    <div className={styles.locationContainer}>
      <MapContainer
        center={[providerCoordinates.lat, providerCoordinates.lng]}
        zoom={14}
        style={{ height: mapHeight, width: '100%', borderRadius: '8px' }}
        className={styles.map}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {/* Provider marker */}
        <Marker position={[providerCoordinates.lat, providerCoordinates.lng]} icon={providerIcon}>
          <Popup>
            <div style={{ textAlign: 'center' }}>
              <strong>{providerName}</strong><br />
              {providerAddress.street}<br />
              {providerAddress.city}, {providerAddress.state}
            </div>
          </Popup>
        </Marker>
        
        {/* User location marker */}
        {userLocation && (
          <Marker position={[userLocation.lat, userLocation.lng]} icon={userLocationIcon}>
            <Popup>
              <div style={{ textAlign: 'center' }}>
                <strong>Your Location</strong>
              </div>
            </Popup>
          </Marker>
        )}
        
        {/* Adjust map bounds to show both markers */}
        <MapBounds providerLocation={providerCoordinates} userLocation={userLocation} />
      </MapContainer>
      
      <button className={styles.directionsButton} onClick={handleDirections}>
        <DirectionsIcon />
        Get Directions
      </button>
    </div>
  );
};

export default LocationDirections;